// src/components/dashboard/FinancialOverviewPanel.tsx
import React, { useState, useEffect } from "react";
import {
    Typography,
    Box,
    Paper,
    Grid,
    Divider,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    LinearProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import EuroIcon from "@mui/icons-material/Euro";
import PaymentIcon from "@mui/icons-material/Payment";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PieChartIcon from "@mui/icons-material/PieChart";
import axiosInstance from "../../utils/axiosConfig";
import PrintableFinancialOverview from "./PrintableFinancialOverview";

import logger from "../../utils/logger";

interface FinancialOverviewPanelProps {
    competitionId: string;
}

// Finanzübersicht Interface
interface FinancialSummary {
    totalRegistrationFees: number;      // Reine Meldegebühren (Elite + Basis + Strafgebühren)
    totalTShirtFees: number;           // T-Shirt Gebühren
    totalCateringFees: number;         // Verpflegungsgebühren
    totalOvernightFees: number;        // Übernachtungsgebühren
    totalAmount: number;               // Gesamtsumme aller Gebühren
    totalPaidAmount: number;           // Bereits eingegangene Zahlungen
    totalOutstandingAmount: number;    // Noch offene Beträge
    paymentsByStatus: {
        paid: { count: number; amount: number };
        partial: { count: number; amount: number };
        unpaid: { count: number; amount: number };
    };
}

// Detaillierte Vereinsübersicht (optional für erweiterte Ansicht)
interface ClubFinancialDetail {
    clubId: string;
    clubName: string;
    fees: {
        registrationFees: number;
        tShirtFees: number;
        cateringFees: number;
        overnightFees: number;
        totalAmount: number;
    };
    payment?: {
        amount: number;
        status: 'paid' | 'partial' | 'unpaid';
        receivedDate?: Date;
    };
    outstandingAmount: number;
}

const FinancialOverviewPanel: React.FC<FinancialOverviewPanelProps> = ({ competitionId }) => {
    const [financialSummary, setFinancialSummary] = useState<FinancialSummary | null>(null);
    const [clubDetails, setClubDetails] = useState<ClubFinancialDetail[]>([]);
    const [competitionName, setCompetitionName] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [showDetails, setShowDetails] = useState<boolean>(false);

    // Fetch financial data
    useEffect(() => {
        const fetchFinancialData = async () => {
            try {
                setLoading(true);
                setError(null);
                
                // Competition-Name laden
                try {
                    const competitionResponse = await axiosInstance.get(`/api/competitions/${competitionId}`);
                    setCompetitionName(competitionResponse.data.name || 'Unbekannter Wettkampf');
                } catch (compError) {
                    //console.log('Competition name not available:', compError);
                    logger.info('Competition name not available:', {error: compError});
                    setCompetitionName('Wettkampf');
                }
                
                // Basis-Finanzübersicht laden
                const summaryResponse = await axiosInstance.get(
                    `/api/competition-registrations/${competitionId}/financial-summary`
                );
                setFinancialSummary(summaryResponse.data);

                // Optional: Detaillierte Vereinsdaten laden
                try {
                    const detailsResponse = await axiosInstance.get(
                        `/api/competition-registrations/${competitionId}/financial-details`
                    );
                    if (detailsResponse.data?.clubDetails) {
                        setClubDetails(detailsResponse.data.clubDetails);

                        // Korrigiere Finanz-Statistiken basierend auf aktuellen Beträgen
                        if (clubDetails.length > 0) {
                            const correctedSummary = getCorrectedFinancialStats(clubDetails, summaryResponse.data);
                            setFinancialSummary(correctedSummary);
                        }
                    }
                } catch (detailError) {
                    //console.log('Details nicht verfügbar:', detailError);
                    logger.info('Details nicht verfügbar:', {error: detailError});
                    // Details sind optional, kein Error werfen
                }
                
            } catch (error) {
                //console.error('Error fetching financial data:', error);
                logger.error('Error fetching financial data:', {error:error});
                setError('Fehler beim Laden der Finanzübersicht');
            } finally {
                setLoading(false);
            }
        };

        if (competitionId) {
            fetchFinancialData();
        }
    }, [competitionId]);

    //Hilfsfunktion: Korrigiere Zahlungsstatus basierend auf aktuellen Beträgen
    const getCorrectPaymentStatus = (club: ClubFinancialDetail): 'paid' | 'partial' | 'unpaid' => {
        if (!club.payment) return 'unpaid';
        
        const paidAmount = Number((club.payment.amount || 0).toFixed(2));
        const expectedAmount = Number(club.fees.totalAmount.toFixed(2));
        
        if (paidAmount === expectedAmount) {
            return 'paid';
        } else if (paidAmount > 0) {
            return 'partial';
        } else {
            return 'unpaid';
        }
    };

    // Hilfsfunktion: Korrigiere Finanz-Statistiken
    const getCorrectedFinancialStats = (clubDetails: ClubFinancialDetail[], originalSummary: FinancialSummary): FinancialSummary => {
        let totalPaidAmount = 0;
        let totalOutstandingAmount = 0;
        
        const paymentsByStatus = {
            paid: { count: 0, amount: 0 },
            partial: { count: 0, amount: 0 },
            unpaid: { count: 0, amount: 0 }
        };
        
        clubDetails.forEach(club => {
            const correctedStatus = getCorrectPaymentStatus(club);
            const paidAmount = club.payment?.amount || 0;
            const expectedAmount = club.fees.totalAmount;
            
            totalPaidAmount += paidAmount;
            
            switch (correctedStatus) {
                case 'paid':
                    paymentsByStatus.paid.count++;
                    paymentsByStatus.paid.amount += paidAmount;
                    break;
                case 'partial':
                    paymentsByStatus.partial.count++;
                    paymentsByStatus.partial.amount += paidAmount;
                    totalOutstandingAmount += expectedAmount - paidAmount;
                    break;
                case 'unpaid':
                    paymentsByStatus.unpaid.count++;
                    totalOutstandingAmount += expectedAmount;
                    break;
            }
        });
        
        return {
            ...originalSummary,
            totalPaidAmount: Math.round(totalPaidAmount * 100) / 100,
            totalOutstandingAmount: Math.round(totalOutstandingAmount * 100) / 100,
            paymentsByStatus: {
                paid: {
                    count: paymentsByStatus.paid.count,
                    amount: Math.round(paymentsByStatus.paid.amount * 100) / 100
                },
                partial: {
                    count: paymentsByStatus.partial.count,
                    amount: Math.round(paymentsByStatus.partial.amount * 100) / 100
                },
                unpaid: {
                    count: paymentsByStatus.unpaid.count,
                    amount: Math.round(paymentsByStatus.unpaid.amount * 100) / 100
                }
            }
        };
    };

    // Hilfsfunktionen
    const getPaymentStatusColor = (status: string) => {
        switch (status) {
            case 'paid': return 'success';
            case 'partial': return 'warning';
            case 'unpaid': return 'error';
            default: return 'default';
        }
    };

    const getPaymentStatusLabel = (status: string) => {
        switch (status) {
            case 'paid': return 'Bezahlt';
            case 'partial': return 'Teilweise';
            case 'unpaid': return 'Offen';
            default: return 'Unbekannt';
        }
    };

    const formatCurrency = (amount: number) => {
        return `${amount.toFixed(2)} €`;
    };

    const calculatePaymentProgress = () => {
        if (!financialSummary || financialSummary.totalAmount === 0) return 0;
        return (financialSummary.totalPaidAmount / financialSummary.totalAmount) * 100;
    };

    const getProgressColor = (progress: number) => {
        if (progress >= 80) return 'success';
        if (progress >= 50) return 'warning';
        return 'error';
    };

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                    <Typography variant="body2" sx={{ ml: 2 }}>
                        Lade Finanzübersicht...
                    </Typography>
                </Box>
            </Box>
        );
    }

    if (error || !financialSummary) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    {error || 'Finanzübersicht nicht verfügbar'}
                </Alert>
            </Box>
        );
    }

    const paymentProgress = calculatePaymentProgress();

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2 
            }}>
                <Box>
                    <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        <AccountBalanceIcon sx={{ mr: 1 }} />
                        Finanzübersicht
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Umfassende Übersicht über alle Gebühren und Zahlungseingänge des Wettkampfs.
                    </Typography>
                </Box>
                {!loading && financialSummary && clubDetails.length > 0 && (
                    <PrintableFinancialOverview
                        financialSummary={financialSummary}
                        clubDetails={clubDetails}
                        competitionName={competitionName}
                    />
                )}
            </Box>

            {/* Haupt-Kennzahlen */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Gesamtsumme */}
                <Grid item xs={12} md={3}>
                    <Card elevation={2}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <EuroIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                            <Typography variant="h4" color="primary" fontWeight="bold">
                                {formatCurrency(financialSummary.totalAmount)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Gesamtsumme
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Eingegangene Zahlungen */}
                <Grid item xs={12} md={3}>
                    <Card elevation={2}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <CheckCircleIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
                            <Typography variant="h4" color="success.main" fontWeight="bold">
                                {formatCurrency(financialSummary.totalPaidAmount)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Eingegangen
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Offene Beträge */}
                <Grid item xs={12} md={3}>
                    <Card elevation={2}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <WarningIcon color="error" sx={{ fontSize: 40, mb: 1 }} />
                            <Typography variant="h4" color="error.main" fontWeight="bold">
                                {formatCurrency(financialSummary.totalOutstandingAmount)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Ausstehend
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Zahlungsquote */}
                <Grid item xs={12} md={3}>
                    <Card elevation={2}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <TrendingUpIcon 
                                color={getProgressColor(paymentProgress)} 
                                sx={{ fontSize: 40, mb: 1 }} 
                            />
                            <Typography 
                                variant="h4" 
                                color={`${getProgressColor(paymentProgress)}.main`}
                                fontWeight="bold"
                            >
                                {paymentProgress.toFixed(1)}%
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Zahlungsquote
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Zahlungsfortschritt */}
            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Zahlungsfortschritt
                </Typography>
                <Box sx={{ mb: 2 }}>
                    <LinearProgress 
                        variant="determinate" 
                        value={paymentProgress} 
                        color={getProgressColor(paymentProgress)}
                        sx={{ height: 10, borderRadius: 5 }}
                    />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2">
                        {formatCurrency(financialSummary.totalPaidAmount)} von {formatCurrency(financialSummary.totalAmount)} eingegangen
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                        {paymentProgress.toFixed(1)}%
                    </Typography>
                </Box>
            </Paper>

            <Grid container spacing={3}>
                {/* Gebühren-Aufschlüsselung */}
                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                            <PieChartIcon sx={{ mr: 1 }} />
                            Gebühren-Aufschlüsselung
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body1">Meldegebühren:</Typography>
                                <Chip 
                                    label={formatCurrency(financialSummary.totalRegistrationFees)}
                                    color="primary"
                                    variant="outlined"
                                />
                            </Box>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body1">T-Shirt Gebühren:</Typography>
                                <Chip 
                                    label={formatCurrency(financialSummary.totalTShirtFees)}
                                    color="info"
                                    variant="outlined"
                                />
                            </Box>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body1">Verpflegung:</Typography>
                                <Chip
                                    label={formatCurrency(financialSummary.totalCateringFees)}
                                    color="warning"
                                    variant="outlined"
                                />
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body1">Übernachtung:</Typography>
                                <Chip
                                    label={formatCurrency(financialSummary.totalOvernightFees)}
                                    color="secondary"
                                    variant="outlined"
                                />
                            </Box>

                            <Divider />
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="h6" fontWeight="bold">Gesamtsumme:</Typography>
                                <Chip 
                                    label={formatCurrency(financialSummary.totalAmount)}
                                    color="primary"
                                    sx={{ fontWeight: 'bold', fontSize: '1.1em' }}
                                />
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                {/* Zahlungsstatus-Übersicht */}
                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                            <PaymentIcon sx={{ mr: 1 }} />
                            Zahlungsstatus
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body1">Vollständig bezahlt:</Typography>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Chip 
                                        label={`${financialSummary.paymentsByStatus.paid.count} Vereine`}
                                        color="success"
                                        size="small"
                                    />
                                    <Chip 
                                        label={formatCurrency(financialSummary.paymentsByStatus.paid.amount)}
                                        color="success"
                                        variant="outlined"
                                    />
                                </Box>
                            </Box>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body1">Teilweise bezahlt:</Typography>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Chip 
                                        label={`${financialSummary.paymentsByStatus.partial.count} Vereine`}
                                        color="warning"
                                        size="small"
                                    />
                                    <Chip 
                                        label={formatCurrency(financialSummary.paymentsByStatus.partial.amount)}
                                        color="warning"
                                        variant="outlined"
                                    />
                                </Box>
                            </Box>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body1">Unbezahlt:</Typography>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Chip 
                                        label={`${financialSummary.paymentsByStatus.unpaid.count} Vereine`}
                                        color="error"
                                        size="small"
                                    />
                                    <Chip 
                                        label={formatCurrency(financialSummary.paymentsByStatus.unpaid.amount)}
                                        color="error"
                                        variant="outlined"
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Erweiterte Details (Optional) */}
            {clubDetails.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">
                                Detaillierte Vereinsübersicht ({clubDetails.length} Vereine)
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Verein</TableCell>
                                            <TableCell align="right">Meldegebühren</TableCell>
                                            <TableCell align="right">T-Shirts</TableCell>
                                            <TableCell align="right">Verpflegung</TableCell>
                                            <TableCell align="right">Übernachtung</TableCell>
                                            <TableCell align="right">Gesamt</TableCell>
                                            <TableCell align="center">Status</TableCell>
                                            <TableCell align="right">Eingegangen</TableCell>
                                            <TableCell align="right">Offen</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {clubDetails.map((club) => (
                                            <TableRow key={club.clubId}>
                                                <TableCell>{club.clubName}</TableCell>
                                                <TableCell align="right">
                                                    {formatCurrency(club.fees.registrationFees)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatCurrency(club.fees.tShirtFees)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatCurrency(club.fees.cateringFees)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatCurrency(club.fees.overnightFees)}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                    {formatCurrency(club.fees.totalAmount)}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {(() => {
                                                        const correctedStatus = club.payment ? getCorrectPaymentStatus(club) : 'unpaid';
                                                        const displayLabel = correctedStatus === 'unpaid' && !club.payment ? 'Keine Zahlung' : getPaymentStatusLabel(correctedStatus);
                                                        
                                                        return (
                                                            <Chip 
                                                                label={displayLabel}
                                                                color={getPaymentStatusColor(correctedStatus)}
                                                                size="small"
                                                            />
                                                        );
                                                    })()}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {club.payment 
                                                        ? formatCurrency(club.payment.amount)
                                                        : formatCurrency(0)
                                                    }
                                                </TableCell>
                                                <TableCell align="right" sx={{ 
                                                    color: club.outstandingAmount > 0 ? 'error.main' : 'success.main',
                                                    fontWeight: 'bold'
                                                }}>
                                                    {formatCurrency(club.outstandingAmount)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </AccordionDetails>
                    </Accordion>
                </Box>
            )}
        </Box>
    );
};

export default FinancialOverviewPanel;